import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Center,
  Collapse,
  Container,
  Grid,
  Group,
  Loader,
  Menu,
  Progress,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  Modal,
  Tooltip,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getImport, getImportMappings, startAugmentedImport, checkImportSchema, deleteImport, getImportRowErrors, getGroupingPreview, getCategoryProposals, removeSupersededProducts } from "../api";
import { modals } from "@mantine/modals";
import { useLocation, Link } from "wouter";
import {
  getPimSchemas,
} from "../Pim/pimApi";
import {
  IconArrowRight,
  IconDotsVertical,
  IconExclamationCircle,
  IconEye,
  IconEyeClosed,
  IconTransform,
  IconSparkles,
  IconTrash,
} from "@tabler/icons-react";
import { useStore } from "../store";
import Timeago from "../../../Boilerplate/_Shared/Timeago";
import ImportStatusBadge from "./ImportStatusBadge";
import { StatsGrid } from "./StatsGrid";
import ImportProtoListEmbedded from "./ImportProtoListEmbedded";
import MapToTargetDialog from "./MapToTargetDialog";
import { notifications } from "@mantine/notifications";

/// Backend phase codes that have a translated label; anything else falls back to the raw code.
const ROW_ERROR_PHASES = ["mapping", "groupPrimaryFailed", "exception"];

/// Distinguishes "asked and got nothing" from "not asked yet", so a failed proposal fetch renders
/// as no panel instead of a spinner that never stops. Frozen because it is shared by every render.
const EMPTY_PROPOSALS = Object.freeze({ groups: [], truncated: false });

/// Per-row failure list for the latest augmented run — which rows failed, why, and what
/// data they carried. Loaded on demand when the user expands the failed counter.
function FailedRowsPanel({ importId }) {
  const { t } = useTranslation("imports");
  const [data, setData] = useState(null);

  useEffect(() => {
    getImportRowErrors(importId).then((result) => {
      if (result) setData(result);
    });
  }, [importId]);

  if (!data)
    return (
      <Center py="sm">
        <Loader size="xs" />
      </Center>
    );

  const errors = data.errors || [];
  return (
    <Stack gap={4} mt="sm">
      {data.truncated && (
        <Text size="xs" c="dimmed">
          {t("rowErrors.truncated", { shown: errors.length, total: data.total })}
        </Text>
      )}
      <Table withTableBorder striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={70}>{t("rowErrors.row")}</Table.Th>
            <Table.Th>{t("rowErrors.problem")}</Table.Th>
            <Table.Th>{t("rowErrors.sourceData")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {errors.map((e, idx) => (
            <Table.Tr key={idx}>
              <Table.Td>
                <Text size="sm" fw={600}>{e.rowIndex + 1}</Text>
              </Table.Td>
              <Table.Td>
                <Badge size="xs" color="red" variant="light">
                  {ROW_ERROR_PHASES.includes(e.phase)
                    ? t(`rowErrors.phase.${e.phase}`)
                    : e.phase}
                </Badge>
                <Text size="sm">{e.message}</Text>
              </Table.Td>
              <Table.Td>
                {e.rowValues && (
                  <Tooltip
                    label={Object.entries(e.rowValues)
                      .map(([k, v]) => `${k}: ${v}`)
                      .join("\n")}
                    multiline
                    maw={480}
                  >
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {Object.entries(e.rowValues)
                        .map(([k, v]) => `${k}: ${v}`)
                        .join(" · ")}
                    </Text>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed">
        {t("rowErrors.retryHint")}
      </Text>
    </Stack>
  );
}

/// Pre-flight grouping preview: how many products vs variants the chosen grouping would
/// produce, computed by the same engine the import runs — so a wrong key ("16 rows → 16
/// products") is visible before any credits are spent.
function GroupingPreview({ preview, column }) {
  const { t } = useTranslation("imports");
  if (!preview)
    return (
      <Center py="xs">
        <Loader size="xs" />
      </Center>
    );

  // Known codes get their own wording; anything else is still shown, in red, with the raw code.
  // The panel used to hard-code two codes, so a new one rendered as nothing at all.
  // Backend codes are snake_case; translation keys are camelCase, so i18next never mistakes a
  // trailing "_other"-shaped segment for a plural form.
  const camel = (code) => code.replace(/_(\w)/g, (_, c) => c.toUpperCase());
  const KNOWN = { no_grouping: "red", auto_no_grouping: "yellow", auto_barely_grouping: "yellow" };
  const warnings = preview.warnings || [];
  // An unrecognized code is red on purpose: silence is what let the last grouping defect ship.
  const color = warnings.some((w) => (KNOWN[w] || "red") === "red")
    ? "red"
    : warnings.length > 0
      ? "yellow"
      : "blue";
  const rowsTotal = preview.rowsTotal;
  const basis = preview.axisBasis;

  return (
    <Alert color={color} variant="light" py={8}>
      <Stack gap={4}>
        <Text size="sm">
          <Text span fw={600}>{preview.productCount.toLocaleString()}</Text>{" "}
          {t("grouping.products", { count: preview.productCount })}
          {preview.variantCount > 0 && (
            <>
              {" · "}
              <Text span fw={600}>{preview.variantCount.toLocaleString()}</Text>{" "}
              {t("grouping.variants", { count: preview.variantCount })}
            </>
          )}
          {" "}
          {t("grouping.fromRows", {
            count: rowsTotal,
            rows: rowsTotal.toLocaleString(),
          })}
          {preview.isAutoGrouping && (
            <Text span c="dimmed"> {t("grouping.automatic")}</Text>
          )}
        </Text>
        {warnings.map((code) => (
          <Text key={code} size="sm" c={(KNOWN[code] || "red") === "red" ? "red" : undefined}>
            {KNOWN[code]
              ? t(`grouping.warning.${camel(code)}`, { column })
              : t("grouping.warning.unknown", { code })}
          </Text>
        ))}
        {/* What the automatic rule decided, on both outcomes. Without this the user sees a product
            count and no way to tell whether it came from the supplier's own numbering or from a
            similarity guess that happened to land. */}
        {basis && (
          <Text size="xs" c="dimmed">
            {t("grouping.axisBasis", {
              column: basis.identifierColumn,
              separator: basis.separator,
              axis: basis.axisExamples.join(", "),
              corroborators: basis.corroboratingColumns.join(", "),
            })}
          </Text>
        )}
        {!basis && preview.isAutoGrouping && preview.axisDeclineCode && (
          <Text size="xs" c="dimmed">
            {t(`grouping.decline.${camel(preview.axisDeclineCode)}`, {
              column: preview.axisDeclineColumn || "—",
              defaultValue: t("grouping.decline.unknownReason", {
                code: preview.axisDeclineCode,
              }),
            })}
          </Text>
        )}
        {preview.largestGroups?.length > 0 && (
          <Text size="xs" c="dimmed">
            {t("grouping.largest", {
              groups: preview.largestGroups
                .slice(0, 3)
                .map((g) =>
                  g.sampleTitles[0] && g.sampleTitles[0] !== g.key
                    ? `${g.key} — ${g.sampleTitles[0]} (${g.rowCount})`
                    : `${g.key} (${g.rowCount})`,
                )
                .join(", "),
            })}
          </Text>
        )}
      </Stack>
    </Alert>
  );
}

/// Group the proposals by the product type each group would actually get, so the review scales with
/// the CATALOGUE rather than with the file. Three variants of one product is one decision; two
/// hundred mixed rows is however many distinct categories they landed in, typically ten or twenty,
/// not two hundred. Groups the lexicon could not place go into their own bucket and say so — an
/// invented candidate would be worse than admitting there isn't one.
function bucketProposals(proposals, overrides) {
  const buckets = new Map();
  for (const group of proposals) {
    const top = group.candidates?.[0];
    const chosenId = overrides[group.key] ?? top?.productTypeId ?? null;
    // Candidates carry the label, so a bucket can name itself without another lookup. An override
    // to a type that isn't among this group's candidates still finds its label via any group that
    // did offer it; failing that the bucket shows the raw id, which is at least honest.
    const chosen = group.candidates?.find((c) => c.productTypeId === chosenId);
    const key = chosenId ?? "__unplaced__";

    if (!buckets.has(key)) {
      buckets.set(key, {
        productTypeId: chosenId,
        label: chosen?.label ?? null,
        candidate: chosen ?? null,
        groups: [],
        productCount: 0,
        // Every type offered to any group in this bucket — the options an override can pick from.
        alternatives: new Map(),
      });
    }
    const bucket = buckets.get(key);
    bucket.groups.push(group);
    bucket.productCount += 1;
    if (!bucket.label && chosen?.label) {
      bucket.label = chosen.label;
      bucket.candidate = chosen;
    }
    for (const c of group.candidates || [])
      if (!bucket.alternatives.has(c.productTypeId)) bucket.alternatives.set(c.productTypeId, c);
  }

  // What rides on it first. A bucket covering forty products is worth reading before one covering
  // two, and a thin margin between the top two candidates means the proposal was nearly a coin flip.
  return [...buckets.values()].sort((a, b) => {
    if ((a.productTypeId === null) !== (b.productTypeId === null))
      return a.productTypeId === null ? -1 : 1;
    return b.productCount - a.productCount;
  });
}

/// Margin between a group's best and second-best proposal, as a fraction of the best. Small means
/// the two candidates scored nearly the same and the choice was arbitrary.
function thinMargin(group) {
  const [first, second] = group.candidates || [];
  if (!first || !second || !first.score) return false;
  return (first.score - second.score) / first.score < 0.25;
}

/// Pre-flight category review. Shows what each group would be categorized as before the run starts,
/// and lets the user say otherwise — a pinned group skips its categorize call entirely.
///
/// Nothing here blocks: starting without touching anything behaves exactly as before, except the
/// categorizer gets the proposals as a hint instead of starting blind.
function CategoryReview({ proposals, truncated, overrides, onOverride, loading }) {
  const { t } = useTranslation("imports");
  const [expanded, setExpanded] = useState(null);
  const [showAll, setShowAll] = useState(false);

  if (loading)
    return (
      <Center py="xs">
        <Loader size="xs" />
      </Center>
    );
  // No proposals at all is the normal case for a single-product-type schema, where there is
  // nothing to choose. Say nothing rather than showing an empty panel.
  if (!proposals || proposals.length === 0) return null;

  const buckets = bucketProposals(proposals, overrides);
  const VISIBLE = 6;
  const shown = showAll ? buckets : buckets.slice(0, VISIBLE);
  const pinnedCount = Object.keys(overrides).length;

  return (
    <Alert color="blue" variant="light" py={8}>
      <Stack gap={6}>
        <Group justify="space-between" gap="xs" wrap="nowrap">
          <Text size="sm" fw={500}>{t("category.title")}</Text>
          {pinnedCount > 0 && (
            <Badge size="sm" variant="light" color="green">
              {t("category.pinned", { count: pinnedCount })}
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed">{t("category.hint")}</Text>

        {shown.map((bucket) => {
          const key = bucket.productTypeId ?? "__unplaced__";
          const isOpen = expanded === key;
          const uncertain = bucket.groups.some(thinMargin);

          // The unplaced bucket has no candidates to show and no choice to offer — the AI will
          // decide these. Naming them is the point: they are the ones the preview cannot help with.
          if (bucket.productTypeId === null)
            return (
              <Text key={key} size="sm" c="dimmed">
                {t("category.unplaced", { count: bucket.productCount })}
              </Text>
            );

          return (
            <div key={key}>
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <Badge size="sm" variant="light" style={{ flexShrink: 0 }}>
                  {t("category.products", { count: bucket.productCount })}
                </Badge>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm">
                    {bucket.label ?? bucket.productTypeId}
                    {uncertain && (
                      <Text span size="xs" c="orange"> {t("category.closeCall")}</Text>
                    )}
                  </Text>
                  {bucket.candidate && (
                    <Text size="xs" c="dimmed">
                      {t("category.fieldSummary", {
                        total: bucket.candidate.attributeCount,
                        specific: bucket.candidate.specificAttributes?.length ?? 0,
                        fillable: bucket.candidate.fillableFromFile ?? 0,
                      })}
                    </Text>
                  )}
                </div>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => setExpanded(isOpen ? null : key)}
                  style={{ flexShrink: 0 }}
                >
                  {isOpen ? t("category.hideFields") : t("category.showFields")}
                </Button>
              </Group>

              <Collapse in={isOpen}>
                <Stack gap={4} pl={8} pt={6}>
                  {bucket.candidate?.specificAttributes?.length > 0 && (
                    <Text size="xs">
                      <Text span fw={500}>{t("category.onlyHere")}</Text>{" "}
                      {bucket.candidate.specificAttributes.join(" · ")}
                    </Text>
                  )}
                  {bucket.candidate?.matchedWords?.length > 0 && (
                    <Text size="xs" c="dimmed">
                      {t("category.matchedOn", { words: bucket.candidate.matchedWords.join(", ") })}
                    </Text>
                  )}
                  {/* Overriding applies to the whole bucket. Per-group would be more precise and
                      unreadable at two hundred rows; these groups are here BECAUSE they agree. */}
                  {bucket.alternatives.size > 1 && (
                    <Select
                      size="xs"
                      label={t("category.chooseInstead")}
                      data={[...bucket.alternatives.values()].map((c) => ({
                        value: c.productTypeId,
                        label: `${c.label} — ${t("category.fieldsShort", { count: c.attributeCount })}`,
                      }))}
                      value={bucket.productTypeId}
                      onChange={(val) => {
                        if (!val) return;
                        onOverride(bucket.groups.map((g) => g.key), val);
                      }}
                    />
                  )}
                </Stack>
              </Collapse>
            </div>
          );
        })}

        {buckets.length > VISIBLE && (
          <Button size="compact-xs" variant="subtle" onClick={() => setShowAll(!showAll)}>
            {showAll
              ? t("category.showFewer")
              : t("category.showAll", { count: buckets.length - VISIBLE })}
          </Button>
        )}
        {/* The backend caps how many groups it proposes for. Saying so beats implying it covered
            everything — silent truncation reads as completeness. */}
        {truncated && <Text size="xs" c="dimmed">{t("category.truncated")}</Text>}
      </Stack>
    </Alert>
  );
}

/// Result panel for the most recent augmented-import run, rendered from the backend's
/// durable run record (importData.augmentedImport) — survives refresh and navigation.
function AugmentedRunSummary({ importData, onChanged }) {
  const { t } = useTranslation("imports");
  const run = importData.augmentedImport;
  const [showFailedRows, setShowFailedRows] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  // The poll below outlives a render, so it needs its own answer to "is anyone still watching?".
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // Removing leftovers is a job, and the import's own status does not change while it runs — so
  // this polls until the receipt on the import carries a FinishedAt newer than the one we started
  // with, rather than pretending the work is done the moment the request returns. The backend
  // stamps FinishedAt exactly once, on every terminal path, so a multi-batch removal cannot
  // announce itself finished after its first hundred products.
  const removeSuperseded = async () => {
    const before = importData.supersededCleanup?.finishedAt ?? null;
    setCleaning(true);
    try {
      await removeSupersededProducts(importData.id);
    } catch {
      setCleaning(false);
      return; // error already surfaced by api.js
    }
    for (let attempt = 0; attempt < 90 && mounted.current; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      if (!mounted.current) return;
      let updated;
      try {
        updated = await getImport(importData.id);
      } catch {
        continue;
      }
      if (!mounted.current) return;
      onChanged?.(updated);
      const receipt = updated.supersededCleanup;
      if (receipt?.finishedAt && receipt.finishedAt !== before) {
        setCleaning(false);
        notifications.show(
          receipt.refusal
            ? { color: "orange", title: t("run.removeTitle"), message: receipt.refusal }
            : {
                color: "green",
                title: t("run.removeDoneTitle"),
                message: t("run.removeDone", {
                  removed: receipt.removed ?? 0,
                  skipped: receipt.skippedBecauseChanged ?? 0,
                }),
              },
        );
        return;
      }
    }
    if (mounted.current) setCleaning(false);
  };

  const confirmRemoveSuperseded = () => {
    if (cleaning) return; // a second click before the modal resolves would enqueue twice
    modals.openConfirmModal({
      title: t("run.removeTitle"),
      children: (
        <Stack gap={6}>
          <Text size="sm">{t("run.removeBody", { count: run.orphaned })}</Text>
          <Text size="sm" c="dimmed">{t("run.removeHint")}</Text>
        </Stack>
      ),
      labels: { confirm: t("run.removeConfirm"), cancel: t("run.removeCancel") },
      confirmProps: { color: "red" },
      onConfirm: removeSuperseded,
    });
  };

  if (!run || !run.finishedAt) return null;

  const failed = run.failed > 0;
  const color = importData.status === "failed" ? "red" : failed ? "yellow" : "green";
  const variantConflicts = run.variantConflicts;
  const succeeded = run.succeeded;
  const importId = importData.id;
  // Runs from before this record existed carry no mappedValues at all.
  const mapped = run.mappedValues || {};
  const dropped = (mapped.blank || 0) + (mapped.unknownCode || 0) + (mapped.unconvertible || 0);
  // Runs from before write-if-changed carry no `unchanged` — 0 is the truth for them: they
  // rewrote everything.
  const unchanged = mapped.unchanged || 0;

  return (
    <Alert color={color} variant="light" mb="md" title={t("run.title")}>
      <Group gap="lg">
        <Text size="sm">
          <Text span fw={600}>{run.rowsTotal}</Text> {t("run.rowsIn")}
        </Text>
        {/* Every other counter here is a ROW count, so a run that made one product per row and a
            run that grouped them into families printed identically. `groups` was already recorded
            and simply never shown — which is why 16 rows becoming 16 products looked normal. */}
        {run.groups > 0 && (
          <Text size="sm" c={run.groups === run.rowsTotal && run.rowsTotal > 1 ? "orange" : undefined}>
            <Text span fw={600}>{run.groups}</Text> {t("run.groups", { count: run.groups })}
          </Text>
        )}
        {/* The augmented import writes straight into the PIM, so this counter is the
            only route from here to the products it made. */}
        {succeeded > 0 ? (
          <Link href={`/pim/products?sourceImportId=${importId}`}>
            <Text size="sm" c="blue" style={{ cursor: "pointer" }} td="underline">
              <Text span fw={600}>{succeeded}</Text> {t("run.imported")}
            </Text>
          </Link>
        ) : (
          <Text size="sm">
            <Text span fw={600}>{succeeded}</Text> {t("run.imported")}
          </Text>
        )}
        {run.failed > 0 && (
          <Text
            size="sm"
            c="red"
            td="underline"
            style={{ cursor: "pointer" }}
            onClick={() => setShowFailedRows((v) => !v)}
          >
            <Text span fw={600}>{run.failed}</Text>{" "}
            {showFailedRows ? t("run.failedHide") : t("run.failedShow")}
          </Text>
        )}
        {run.skipped > 0 && (
          <Text size="sm" c="dimmed">
            <Text span fw={600}>{run.skipped}</Text> {t("run.skipped")}
          </Text>
        )}
        {run.uncategorized > 0 && (
          <Link href={`/pim/products?status=RequiresAttention&attentionReason=uncategorized&sourceImportId=${importId}`}>
            <Text size="sm" c="orange" style={{ cursor: "pointer" }} td="underline">
              <Text span fw={600}>{run.uncategorized}</Text> {t("run.uncategorized")}
            </Text>
          </Link>
        )}
        {variantConflicts > 0 && (
          <Link href={`/pim/products?status=RequiresAttention&attentionReason=variantConflict&sourceImportId=${importId}`}>
            <Text size="sm" c="orange" style={{ cursor: "pointer" }} td="underline">
              <Text span fw={600}>{variantConflicts}</Text>{" "}
              {t("run.variantConflicts", { count: variantConflicts })}
            </Text>
          </Link>
        )}
        {/* Advisory findings about the SCHEMA rather than the run: several fields saying the same
            thing, and fields whose rows disagree about the unit. Both are only visible by eye in the
            product grid otherwise, which is how the owner found them. */}
        {run.redundantAttributeGroups > 0 && (
          <Link href={`/pim/products?status=RequiresAttention&attentionReason=redundantAttributes&sourceImportId=${importId}`}>
            <Text size="sm" c="orange" style={{ cursor: "pointer" }} td="underline">
              <Text span fw={600}>{run.redundantAttributeGroups}</Text>{" "}
              {t("run.redundantAttributes", { count: run.redundantAttributeGroups })}
            </Text>
          </Link>
        )}
        {run.unitInconsistencyGroups > 0 && (
          <Link href={`/pim/products?status=RequiresAttention&attentionReason=unitInconsistency&sourceImportId=${importId}`}>
            <Text size="sm" c="orange" style={{ cursor: "pointer" }} td="underline">
              <Text span fw={600}>{run.unitInconsistencyGroups}</Text>{" "}
              {t("run.unitInconsistency", { count: run.unitInconsistencyGroups })}
            </Text>
          </Link>
        )}
        {run.lowConfidenceCategories > 0 && (
          <Link href={`/pim/products?status=RequiresAttention&attentionReason=lowConfidenceCategory&sourceImportId=${importId}`}>
            <Text size="sm" c="orange" style={{ cursor: "pointer" }} td="underline">
              <Text span fw={600}>{run.lowConfidenceCategories}</Text>{" "}
              {t("run.lowConfidenceCategories", { count: run.lowConfidenceCategories })}
            </Text>
          </Link>
        )}
        {/* Products of this file that THIS run did not produce — their key came from an earlier
            run whose grouping differed. Deliberately no status= in the link: the mark is an
            observation, not a demotion, so it never moves a curated product to RequiresAttention
            and a status filter would return an empty list next to a non-zero count. */}
        {run.orphaned > 0 && (
          <Group gap={6} wrap="nowrap">
            <Link href={`/pim/products?attentionReason=notInLatestRun&sourceImportId=${importId}`}>
              <Text size="sm" c="orange" style={{ cursor: "pointer" }} td="underline">
                <Text span fw={600}>{run.orphaned}</Text>{" "}
                {t("run.orphaned", { count: run.orphaned })}
              </Text>
            </Link>
            <Button
              size="compact-xs"
              variant="subtle"
              color="red"
              loading={cleaning}
              onClick={confirmRemoveSuperseded}
            >
              {t("run.remove")}
            </Button>
          </Group>
        )}
        {run.mappingValidationGroups > 0 && (
          <Link href={`/pim/products?status=RequiresAttention&attentionReason=mappingValidation&sourceImportId=${importId}`}>
            <Text size="sm" c="orange" style={{ cursor: "pointer" }} td="underline">
              <Text span fw={600}>{run.mappingValidationGroups}</Text>{" "}
              {t("run.mappingValidation", { count: run.mappingValidationGroups })}
            </Text>
          </Link>
        )}
        {/* Values the mapper answered and the import did not write. Each of these was a silent
            `continue` — the field simply wasn't there afterwards, with nothing anywhere saying so.
            The three reasons need very different actions, so the breakdown is one hover away. */}
        {dropped > 0 && (
          <Tooltip
            multiline
            w={320}
            label={t("run.valuesDroppedDetail", {
              blank: mapped.blank || 0,
              unknownCode: mapped.unknownCode || 0,
              unconvertible: mapped.unconvertible || 0,
            })}
          >
            <Text size="sm" c="orange" style={{ cursor: "help" }}>
              <Text span fw={600}>{dropped}</Text>{" "}
              {t("run.valuesDropped", { count: dropped })}
            </Text>
          </Tooltip>
        )}
        {/* The answer arrived and the catalogue already held exactly it. Shown because otherwise
            "40.000 Zeilen · 3.812 Werte geändert" and "40.000 Zeilen · 1.200.000 Werte geändert"
            read identically, and only one of them is worth an expensive monthly run. */}
        {unchanged > 0 && (
          <Tooltip multiline w={320} label={t("run.valuesUnchangedDetail")}>
            <Text size="sm" c="dimmed" style={{ cursor: "help" }}>
              <Text span fw={600}>{unchanged}</Text>{" "}
              {t("run.valuesUnchanged", { count: unchanged })}
            </Text>
          </Tooltip>
        )}
        {/* Not a defect: a mapper SHOULD leave a field blank when the row says nothing about it.
            Shown dimmed, as the fill rate of the run against the target schema. */}
        {mapped.requested > 0 && (
          <Tooltip multiline w={320} label={t("run.valuesOmittedDetail")}>
            <Text size="sm" c="dimmed" style={{ cursor: "help" }}>
              <Text span fw={600}>{mapped.omitted || 0}</Text>{" "}
              {t("run.valuesOmitted", { requested: mapped.requested })}
            </Text>
          </Tooltip>
        )}
        {run.creditsBilled > 0 && (
          <Text size="sm" c="dimmed">
            <Text span fw={600}>{run.creditsBilled.toLocaleString()}</Text> {t("run.creditsUsed")}
          </Text>
        )}
        <Text size="sm" c="dimmed">
          <Timeago date={run.finishedAt} />
        </Text>
      </Group>
      {run.error && (
        <Text size="sm" c="dimmed" mt={4}>
          {run.error}
        </Text>
      )}
      {/* The receipt outlives the run record that prompted it, so what was destroyed stays
          answerable after the next re-run resets every counter above. */}
      {importData.supersededCleanup?.finishedAt && !importData.supersededCleanup.refusal && (
        <Text size="sm" c="dimmed" mt={4}>
          {t("run.removedReceipt", {
            removed: importData.supersededCleanup.removed ?? 0,
            skipped: importData.supersededCleanup.skippedBecauseChanged ?? 0,
          })}{" "}
          <Timeago date={importData.supersededCleanup.finishedAt} />
        </Text>
      )}
      {showFailedRows && <FailedRowsPanel importId={importId} />}
    </Alert>
  );
}

const MAPPING_STATUSES = ["queued", "processing", "mapped", "failed"];

function MappingStatusBadge({ status }) {
  const { t } = useTranslation("imports");
  let color = "gray";
  let statusText = status.toLowerCase();
  switch (statusText) {
    case "processing":
      color = "blue";
      break;
    case "mapped":
      color = "cyan";
      break;
    case "failed":
      color = "red";
      break;
    case "queued":
    default:
      color = "gray";
  }

  return (
    <Badge
      leftSection={
        status === "processing" ? <Loader size={10} color="white" /> : null
      }
      radius="xs"
      color={color}
      size="sm"
    >
      {MAPPING_STATUSES.includes(statusText)
        ? t(`mappings.status_${statusText}`)
        : status}
    </Badge>
  );
}

function ImportDetails({ params }) {
  const { t } = useTranslation("imports");
  const userProfile = useStore((state) => state.profile);
  const [importData, setImportData] = useState(null);
  const [, setLocation] = useLocation();
  const [importMappings, setImportMappings] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [mapToTargetOpen, setMapToTargetOpen] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [schemasRaw, setSchemasRaw] = useState([]);
  const [augmentedOpen, setAugmentedOpen] = useState(false);
  const [augmentedSchema, setAugmentedSchema] = useState(null);
  const [augmentedRole, setAugmentedRole] = useState("");
  // The role textarea starts collapsed (it is a long paragraph and most runs never touch it),
  // and once the user has typed in it we stop overwriting it from the schema.
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleEdited, setRoleEdited] = useState(false);
  const [augmentedVariantCol, setAugmentedVariantCol] = useState(null);
  // "Normalize + Enrich" toggle: when true the import fills empty attributes via Deep (web) enrichment.
  const [augmentedEnrich, setAugmentedEnrich] = useState(false);
  const [augmentedLoading, setAugmentedLoading] = useState(false);
  const [schemaWarnings, setSchemaWarnings] = useState([]);
  // Credit estimate from the check-schema preflight: { creditsPerRow, rowsTotal,
  // creditsRequired, creditsAvailable, canProceed } — null until a schema is picked.
  const [creditEstimate, setCreditEstimate] = useState(null);
  // Grouping preview (products vs variants) for the chosen grouping column — refreshed
  // whenever the column changes, so a wrong key is visible before spending credits.
  // Null = not loaded yet (the preview panel shows a loader).
  const [groupingPreview, setGroupingPreview] = useState(null);
  // Pre-flight category review: what each group would be categorized as, and the user's overrides
  // keyed on group key. Overrides are deliberately cleared whenever the grouping changes — the keys
  // are derived from the grouping, so a pin made against a different grouping means nothing.
  const [categoryProposals, setCategoryProposals] = useState(null);
  const [typeByGroup, setTypeByGroup] = useState({});

  // The role the CURRENTLY SELECTED schema would supply. Derived rather than stored, so the
  // dialog can offer "back to the schema's role" after the user has typed their own text —
  // without that way back, one hand-written role silently shadows every schema picked
  // afterwards, which is the same trap the old "only fill when empty" guard created.
  const schemaDefaultRole =
    schemasRaw.find((s) => s.id === augmentedSchema)?.defaultAiRole || "";

  useEffect(() => {
    getImport(params.id).then((data) => {
      setImportData(data);
    });
  }, [params.id]);

  // Grouping preview: refetch whenever the dialog is open and the grouping column changes
  // (null column = preview automatic grouping). Independent of the chosen schema. The
  // preview is cleared in the dialog's close/column-change handlers, not here, so the
  // effect never sets state synchronously.
  useEffect(() => {
    if (!augmentedOpen || !params.id) return undefined;
    let cancelled = false;
    getGroupingPreview(params.id, augmentedVariantCol || undefined).then((result) => {
      if (!cancelled && result) setGroupingPreview(result);
    });
    return () => {
      cancelled = true;
    };
  }, [augmentedOpen, augmentedVariantCol, params.id]);

  // Category proposals. Unlike the grouping preview this one needs the target schema, so it waits
  // for one and refetches when either the schema or the grouping changes. Like that one, it clears
  // nothing here — the handlers that invalidate the proposals do the clearing, so this effect never
  // sets state synchronously and cannot cascade renders.
  useEffect(() => {
    if (!augmentedOpen || !params.id || !augmentedSchema) return undefined;
    let cancelled = false;
    getCategoryProposals(params.id, augmentedSchema, augmentedVariantCol || undefined)
      .then((result) => {
        if (!cancelled) setCategoryProposals(result ?? EMPTY_PROPOSALS);
      })
      .catch(() => {
        // A failed proposal fetch must not stop anyone importing, and must not leave a spinner
        // running forever. An empty result renders as no panel at all, so the dialog behaves
        // exactly as it did before this feature existed.
        if (!cancelled) setCategoryProposals(EMPTY_PROPOSALS);
      });
    return () => {
      cancelled = true;
    };
  }, [augmentedOpen, augmentedSchema, augmentedVariantCol, params.id]);

  // Run state is derived from FETCHED data — the backend's durable run record is the source
  // of truth, so a refresh or navigation never loses visibility of an in-flight run.
  const running = importData?.status === "processing";

  // Poll while a run is in flight; notify once on the transition to a terminal state.
  useEffect(() => {
    if (!running) return undefined;
    // Slow responses can leave two poll callbacks in flight; only the first may notify
    // (clearInterval doesn't abort an already-running callback).
    let notified = false;
    const poll = setInterval(async () => {
      try {
        const updated = await getImport(params.id);
        setImportData(updated);
        if (updated.status !== "processing") {
          clearInterval(poll);
          if (notified) return;
          notified = true;
          const run = updated.augmentedImport;
          notifications.show(
            updated.status === "succeeded"
              ? {
                  title: t("notify.completeTitle"),
                  message:
                    run?.succeeded != null
                      ? t("notify.completeMessage", { count: run.succeeded })
                      : t("notify.completeMessageAll"),
                  color: "green",
                }
              : updated.status === "completedWithErrors"
                ? {
                    title: t("notify.errorsTitle"),
                    message: run?.error || t("notify.errorsMessage"),
                    color: "yellow",
                  }
                : {
                    title: t("notify.failedTitle"),
                    message: run?.error || t("notify.failedMessage"),
                    color: "red",
                  },
          );
        }
      } catch {
        clearInterval(poll);
      }
    }, 3000);
    return () => clearInterval(poll);
  }, [running, params.id, t]);

  useEffect(() => {
    getImportMappings(params.id).then((data) => {
      setImportMappings(data);
    });
  }, [params.id]);

  // Load schemas for display in the table
  useEffect(() => {
    if (userProfile?.tenantId) {
      getPimSchemas(userProfile.tenantId)
        .then((data) => {
          setSchemasRaw(data || []);
          const formattedData = (data || []).map((item) => ({
            value: item.id,
            label: item.labels?.en || item.name,
          }));
          setSchemas(formattedData);
        })
        .catch(() => {
          // Silently fail - schemas will be empty
        });
    }
  }, [userProfile?.tenantId]);


  const handleDelete = async () => {
    if (!window.confirm(t("actions.deleteConfirm"))) return;
    try {
      await deleteImport(importData.id);
      setLocation("/imports");
    } catch {
      // Error shown by api.js
    }
  };

  // Helper to get schema name by ID
  const getSchemaName = (schemaId) => {
    if (!schemaId) return null;
    const schema = schemas.find((s) => s.value === schemaId);
    return schema?.label || (schemas.length === 0 ? t("meta.loadingSchemas") : schemaId.substring(0, 8) + "...");
  };

  if (!importData) {
    return (
      <Container size="xs">
        <Center>
          <Loader />
        </Center>
      </Container>
    );
  }

  const existingMappings = importMappings?.map(
    (p) => p.destinationSchemaId || p.destinationMarketplaceId,
  );
  const availableSchemas =
    schemas?.filter((schema) => !existingMappings?.includes(schema.value)) ??
    [];

  const keys = importData.columns?.map((p) => p.key);
  if (!keys) {
    return <div>{t("meta.noColumns")}</div>;
  }

  return (
    <Container size={showDetails ? null : "md"} fluid={showDetails}>
      <Grid>
        <Grid.Col span={showDetails ? 2 : 12} p="md">
          {/* One primary action. Mapping to a target schema and deleting are rarer and
              live in the kebab, so the augmented import is the obvious thing to click. */}
          <Group justify="space-between" mb="lg" wrap="nowrap">
            <Group gap="sm">
              {importData.schemaId && (
                <Button
                  size="sm"
                  color="grape"
                  leftSection={<IconSparkles size={18} />}
                  onClick={() => setAugmentedOpen(true)}
                  disabled={running}
                >
                  {t("actions.augmentedImport")}
                </Button>
              )}
              <Button
                size="sm"
                variant="default"
                rightSection={
                  showDetails ? (
                    <IconEyeClosed size={18} />
                  ) : (
                    <IconEye size={18} />
                  )
                }
                onClick={() => setShowDetails((prev) => !prev)}
              >
                {showDetails ? t("actions.hideDetails") : t("actions.showDetails")}
              </Button>
            </Group>
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon
                  size="lg"
                  variant="subtle"
                  color="gray"
                  aria-label={t("actions.more")}
                >
                  <IconDotsVertical size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {importData.schemaId && (
                  <Menu.Item
                    leftSection={<IconArrowRight size={16} />}
                    onClick={() => setMapToTargetOpen(true)}
                  >
                    {t("actions.mapToTarget")}
                  </Menu.Item>
                )}
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  onClick={handleDelete}
                >
                  {t("actions.delete")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          {running && (
            <Alert
              icon={<Loader size={18} />}
              color="grape"
              variant="light"
              mb="md"
            >
              <Text fw={500}>{t("progress.title")}</Text>
              <Text size="sm" c="dimmed">
                {t("progress.hint")}
                {importData.augmentedImport &&
                  " " +
                    t("progress.rowsProcessed", {
                      done:
                        importData.augmentedImport.succeeded +
                        importData.augmentedImport.failed,
                      total: importData.augmentedImport.rowsTotal,
                    })}
              </Text>
              <Progress
                value={(importData.augmentedImportProgress ?? 0) * 100}
                mt="xs"
                color="grape"
                animated
              />
            </Alert>
          )}

          <AugmentedRunSummary importData={importData} onChanged={setImportData} />

          <Group align="center" justify="space-between" mb="md">
            <Stack gap={3}>
              <Text fw={600}>{importData.originalName}</Text>
              <Group gap="xs">
                <Text fz="sm" c="dimmed">
                  <Timeago date={importData.created} />
                </Text>
                {importData.schemaId && (
                  <>
                    <Text fz="sm" c="dimmed">
                      •
                    </Text>
                    <Text fz="sm" c="dimmed">
                      {t("meta.schemaLabel")}{" "}
                      <Link href={`/pim/schemas/${importData.schemaId}`}>
                        <Text span c="blue" style={{ cursor: "pointer" }}>
                          {getSchemaName(importData.schemaId) || t("meta.viewSchema")}
                        </Text>
                      </Link>
                    </Text>
                  </>
                )}
              </Group>
            </Stack>

            <ImportStatusBadge status={importData.status} />
          </Group>

          <StatsGrid
            vertical={showDetails}
            importData={importData}
            mappings={importMappings}
          />

          {!showDetails && (
            <>
              {/* Mapping profiles are a separate pipeline from the augmented import — they
                  produce MappingProcess records, which is all this table reads. An augmented
                  run therefore never lands here, hence the hint. */}
              <Group justify="space-between" align="center" mt="lg" mb={4} wrap="nowrap">
                <Text fw={600} size="sm">{t("mappings.sectionTitle")}</Text>
                {importData.schemaId && (
                  <Button
                    size="xs"
                    variant="default"
                    leftSection={<IconArrowRight size={16} />}
                    onClick={() => setMapToTargetOpen(true)}
                  >
                    {t("actions.mapToTarget")}
                  </Button>
                )}
              </Group>
              <Text size="xs" c="dimmed" mb="sm">
                {t("mappings.sectionHint")}
              </Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("mappings.targetSchema")}</Table.Th>
                    <Table.Th>{t("mappings.status")}</Table.Th>
                    <Table.Th>{t("mappings.created")}</Table.Th>
                    <Table.Th ta="right">{t("mappings.products")}</Table.Th>
                    <Table.Th ta="right">{t("mappings.mapped")}</Table.Th>
                    <Table.Th ta="right">{t("mappings.errors")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {importMappings?.length === 0 && (
                    <Table.Tr>
                      <Table.Td ta="center" colSpan={7}>
                        <Stack p="lg" align="center" gap="xs">
                          <IconTransform size={40} stroke={1} />
                          <Text align="center" c="dimmed" size="sm">
                            {t("mappings.empty")}
                          </Text>
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {importMappings?.map((mapping) => (
                    <Table.Tr key={mapping.id}>
                      <Table.Td>
                        {getSchemaName(mapping.destinationSchemaId) || "—"}
                      </Table.Td>
                      <Table.Td>
                        <MappingStatusBadge status={mapping.status} />
                      </Table.Td>
                      <Table.Td>
                        <Timeago date={mapping.idCreated} />
                      </Table.Td>
                      <Table.Td ta="right">{mapping.outputCount}</Table.Td>
                      <Table.Td ta="right">
                        {mapping.mappedProductCount}
                      </Table.Td>
                      <Table.Td ta="right">
                        {mapping.errorProductCount > 0 ? (
                          <Tooltip
                            label={
                              mapping.abortReason
                                ? t("mappings.aborted", { reason: mapping.abortReason })
                                : t("mappings.hadErrors")
                            }
                            withArrow
                          >
                            <Group gap={6} align="center" justify="flex-end">
                              <IconExclamationCircle size={16} color="red" />
                              {mapping.errorProductCount}
                            </Group>
                          </Tooltip>
                        ) : (
                          <Text c="dimmed" ta="right">
                            -
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        <Group justify="flex-end" gap={4}>
                          {mapping.errorProductCount > 0 && (
                            <Button
                              component={Link}
                              to={`/imports/${importData.id}/mappings/${mapping.id}`}
                              rightSection={<IconArrowRight size={16} />}
                              variant="default"
                              size="xs"
                            >
                              {t("mappings.viewErrors")}
                            </Button>
                          )}

                          {mapping.outputCount > 0 && (
                            <Button
                              component={Link}
                              to={`/imports/${importData.id}/mappings/${mapping.id}/products`}
                              rightSection={<IconArrowRight size={16} />}
                              variant="default"
                              size="xs"
                            >
                              {t("mappings.viewScores")}
                            </Button>
                          )}

                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </>
          )}

          {/* Map to Target Dialog */}
          <MapToTargetDialog
            opened={mapToTargetOpen}
            onClose={() => setMapToTargetOpen(false)}
            importData={importData}
          />

          {/* Augmented Import Dialog */}
          <Modal
            opened={augmentedOpen}
            onClose={() => {
              setAugmentedOpen(false);
              setGroupingPreview(null);
              setCategoryProposals(null);
              setTypeByGroup({});
            }}
            title={t("augmented.title")}
            centered
          >
            <Stack>
              <Text size="sm" c="dimmed">
                {t("augmented.intro")}
              </Text>
              <Select
                label={t("augmented.targetSchema")}
                placeholder={t("augmented.selectSchema")}
                data={schemas}
                value={augmentedSchema}
                onChange={async (val) => {
                  setAugmentedSchema(val);
                  // The proposals are per schema; the pins are keyed on groups of that schema.
                  // Clearing here rather than in the effect keeps the effect side-effect free.
                  setCategoryProposals(null);
                  setTypeByGroup({});
                  setSchemaWarnings([]);
                  setCreditEstimate(null);
                  // Pre-fill the role from the schema's defaultAiRole. The old guard was "only
                  // when the box is empty", so after switching schemas the FIRST schema's role
                  // stuck and was sent with the second schema's run. Follow the selection
                  // instead, and stop following only once the user has typed their own text.
                  const raw = schemasRaw.find((s) => s.id === val);
                  if (!roleEdited) setAugmentedRole(raw?.defaultAiRole || "");
                  // Check schema completeness + credit estimate (reflects the enrichment mode)
                  if (val) {
                    try {
                      const result = await checkImportSchema(
                        importData.id, val, augmentedEnrich ? "Deep" : null);
                      setSchemaWarnings(result?.warnings || []);
                      if (result?.creditsRequired != null) setCreditEstimate(result);
                    } catch { /* ignore */ }
                  }
                }}
                searchable
              />
              <Select
                label={t("augmented.variantColumn")}
                description={t("augmented.variantColumnHint")}
                placeholder={t("augmented.variantColumnPlaceholder")}
                data={importData.columns?.map((c) => ({ value: c.name, label: c.name })) || []}
                value={augmentedVariantCol}
                onChange={(val) => {
                  // Clear the stale preview so it doesn't flash while the new one loads.
                  setGroupingPreview(null);
                  // Group keys are derived from the grouping, so every pin made against the old
                  // one is meaningless the moment the column changes.
                  setCategoryProposals(null);
                  setTypeByGroup({});
                  setAugmentedVariantCol(val);
                }}
                clearable
                searchable
              />
              <GroupingPreview
                preview={groupingPreview}
                column={augmentedVariantCol}
              />
              {/* Directly under the grouping, because it is a decision ABOUT those groups and the
                  keys it pins are the ones the panel above just showed. */}
              <CategoryReview
                proposals={categoryProposals?.groups}
                truncated={categoryProposals?.truncated}
                overrides={typeByGroup}
                loading={!!augmentedSchema && categoryProposals === null}
                onOverride={(keys, productTypeId) =>
                  setTypeByGroup((prev) => {
                    const next = { ...prev };
                    for (const k of keys) next[k] = productTypeId;
                    return next;
                  })
                }
              />
              <div>
                <Text size="sm" fw={500} mb={4}>{t("augmented.dataLabel")}</Text>
                <SegmentedControl
                  fullWidth
                  value={augmentedEnrich ? "enrich" : "normalize"}
                  onChange={async (val) => {
                    const enrich = val === "enrich";
                    setAugmentedEnrich(enrich);
                    // Re-price: enrichment adds a per-product cost.
                    if (augmentedSchema) {
                      try {
                        const result = await checkImportSchema(
                          importData.id, augmentedSchema, enrich ? "Deep" : null);
                        setSchemaWarnings(result?.warnings || []);
                        if (result?.creditsRequired != null) setCreditEstimate(result);
                      } catch { /* ignore */ }
                    }
                  }}
                  data={[
                    { label: t("augmented.normalizeOnly"), value: "normalize" },
                    { label: t("augmented.normalizeEnrich"), value: "enrich" },
                  ]}
                />
                <Text size="xs" c="dimmed" mt={4}>
                  {augmentedEnrich
                    ? t("augmented.enrichHint")
                    : t("augmented.normalizeHint")}
                </Text>
              </div>
              {/* The AI role was already prefilled from the schema and posted with the run, but
                  there was no input for it anywhere — so the naming and tone convention that
                  decides how every title reads was invisible and unchangeable. Collapsed by
                  default: it is a paragraph, and most runs just use what the schema says. */}
              <div>
                <Group justify="space-between" align="center" gap="xs">
                  <Text size="sm" fw={500}>{t("augmented.roleLabel")}</Text>
                  <Group gap={4}>
                    {roleEdited && schemaDefaultRole !== augmentedRole && (
                      <Button
                        variant="subtle"
                        color="gray"
                        size="compact-xs"
                        onClick={() => {
                          setAugmentedRole(schemaDefaultRole);
                          setRoleEdited(false);
                        }}
                      >
                        {t("augmented.roleReset")}
                      </Button>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      onClick={() => setRoleOpen((open) => !open)}
                    >
                      {roleOpen ? t("augmented.roleHide") : t("augmented.roleShow")}
                    </Button>
                  </Group>
                </Group>
                {!roleOpen && (
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {augmentedRole || t("augmented.roleEmpty")}
                  </Text>
                )}
                <Collapse expanded={roleOpen}>
                  <Textarea
                    autosize
                    minRows={3}
                    maxRows={8}
                    placeholder={t("augmented.rolePlaceholder")}
                    value={augmentedRole}
                    onChange={(event) => {
                      // Any keystroke means the text belongs to the user from here on, so a
                      // later schema switch must not overwrite it.
                      setRoleEdited(true);
                      setAugmentedRole(event.currentTarget.value);
                    }}
                  />
                  <Text size="xs" c="dimmed" mt={4}>
                    {t("augmented.roleHint")}
                  </Text>
                </Collapse>
              </div>
              {schemaWarnings.length > 0 && (
                <Alert
                  color="yellow"
                  title={t("augmented.schemaWarnings")}
                  icon={<IconExclamationCircle size={18} />}
                >
                  {schemaWarnings.map((w, i) => (
                    <Text key={i} size="sm">{w}</Text>
                  ))}
                </Alert>
              )}
              {creditEstimate && !creditEstimate.canProceed && (
                <Alert color="red" title={t("augmented.insufficientTitle")} icon={<IconExclamationCircle size={18} />}>
                  {t(
                    creditEstimate.enrichCreditsPerProduct > 0
                      ? "augmented.insufficientBodyEnrich"
                      : "augmented.insufficientBody",
                    {
                      required: creditEstimate.creditsRequired.toLocaleString(),
                      rows: creditEstimate.rowsTotal.toLocaleString(),
                      perRow: creditEstimate.creditsPerRow,
                      enrich: creditEstimate.enrichCreditsPerProduct,
                      available: creditEstimate.creditsAvailable.toLocaleString(),
                    },
                  )}
                </Alert>
              )}
              <Text size="xs" c="dimmed">
                {creditEstimate
                  ? t(
                      creditEstimate.enrichCreditsPerProduct > 0
                        ? "augmented.estimateEnrich"
                        : "augmented.estimate",
                      {
                        rows: creditEstimate.rowsTotal.toLocaleString(),
                        credits: creditEstimate.creditsRequired.toLocaleString(),
                        perRow: creditEstimate.creditsPerRow,
                        enrich: creditEstimate.enrichCreditsPerProduct,
                      },
                    )
                  : t("augmented.estimateUnknown", {
                      rows: importData.rowCount || "?",
                    })}
              </Text>
              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  onClick={() => setAugmentedOpen(false)}
                >
                  {t("augmented.cancel")}
                </Button>
                <Button
                  color="grape"
                  leftSection={<IconSparkles size={18} />}
                  loading={augmentedLoading}
                  disabled={!augmentedSchema || (creditEstimate ? !creditEstimate.canProceed : false)}
                  onClick={async () => {
                    setAugmentedLoading(true);
                    try {
                      await startAugmentedImport(importData.id, {
                        schemaId: augmentedSchema,
                        role: augmentedRole || undefined,
                        variantGroupColumn: augmentedVariantCol || undefined,
                        enrichmentMode: augmentedEnrich ? "Deep" : undefined,
                        // Only what the user actually changed. Sending the proposals themselves
                        // would turn a suggestion into a decision nobody made.
                        productTypeByGroup: Object.keys(typeByGroup).length > 0 ? typeByGroup : undefined,
                      });

                      setAugmentedOpen(false);
                      // The backend flips the import to 'processing' and seeds the run record
                      // before returning — refetch and let the poll effect take over.
                      setImportData(await getImport(importData.id));
                    } catch {
                      // Error shown by api.js (409 = a run is already in flight)
                    } finally {
                      setAugmentedLoading(false);
                    }
                  }}
                >
                  {t("augmented.start")}
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Grid.Col>
        {showDetails && (
          <Grid.Col span={10} p="md">
            <ImportProtoListEmbedded importData={importData} />
          </Grid.Col>
        )}
      </Grid>
    </Container>
  );
}

export default ImportDetails;
